import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MessageSquare, Paperclip, Mail, AlertCircle, X, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { FormComponentType as TFormComponent } from './df-form-controls/df-form-preview-interfaces';
import { RaiseIssueModal } from './components/RaiseIssueModal';
import './ComponentActionFeatures.scss';

// Attachment Thumbnails Component
const AttachmentThumbnails: React.FC<{
  attachments: File[] | null;
  onRemove: (index: number) => void;
}> = ({ attachments, onRemove }) => {
  const [objectUrls, setObjectUrls] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    if (!attachments) {
      // Cleanup all URLs
      setObjectUrls(prev => {
        prev.forEach(url => URL.revokeObjectURL(url));
        return new Map();
      });
      return;
    }

    const newUrls = new Map<number, string>();
    attachments.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        newUrls.set(index, url);
      }
    });

    // Cleanup old URLs that are no longer needed
    setObjectUrls(prev => {
      prev.forEach((url, index) => {
        if (!newUrls.has(index)) {
          URL.revokeObjectURL(url);
        }
      });
      return newUrls;
    });

    // Cleanup on unmount
    return () => {
      newUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [attachments]);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <div className="attachment-thumbnails-container">
      {attachments.map((file, index) => {
        const isImage = file.type.startsWith('image/');
        const objectUrl = objectUrls.get(index);
        
        return (
          <div key={index} className="attachment-thumbnail">
            {isImage && objectUrl ? (
              <>
                <img src={objectUrl} alt={file.name} />
                <button
                  type="button"
                  className="thumbnail-remove-btn"
                  onClick={() => onRemove(index)}
                  title="Remove attachment"
                >
                  <X size={14} />
                </button>
              </>
            ) : (
              <>
                <div className="file-icon-placeholder">
                  <Paperclip size={20} />
                </div>
                <span className="file-name">{file.name}</span>
                <button
                  type="button"
                  className="thumbnail-remove-btn"
                  onClick={() => onRemove(index)}
                  title="Remove attachment"
                >
                  <X size={14} />
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

interface IComponentActionFeaturesProps {
  component: TFormComponent;
  mode: 'edit' | 'preview' | 'test';
  formTemplateId?: string;
  formValue?: any;
  onNotesChange?: (notes: string) => void;
  onAttachmentChange?: (files: File[] | null) => void;
  notes?: string;
  attachments?: File[] | null;
  onThresholdActionCompletion?: (conditionId: string, action: 'notes' | 'attachments' | 'email', completed: boolean) => void;
  onThresholdIssueRaised?: (conditionId: string) => void;
  onBasicPropertyActionCompletion?: (action: 'notes' | 'attachments' | 'email' | 'issue', completed: boolean) => void;
}

export const ComponentActionFeatures: React.FC<IComponentActionFeaturesProps> = ({
  component,
  mode,
  formTemplateId,
  formValue,
  onNotesChange,
  onAttachmentChange,
  notes = '',
  attachments = null,
  onThresholdActionCompletion,
  onThresholdIssueRaised,
  onBasicPropertyActionCompletion
}) => {
  const [localNotes, setLocalNotes] = useState<string>(notes || '');
  const [localAttachments, setLocalAttachments] = useState<File[] | null>(attachments || null);
  const [showNotesInput, setShowNotesInput] = useState<boolean>(false);
  const [showRaiseIssueModal, setShowRaiseIssueModal] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [issueRaised, setIssueRaised] = useState<boolean>(false);
  const [actionsExpanded, setActionsExpanded] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previousFormValueRef = useRef<any>(formValue);
  const notesInitializedRef = useRef<boolean>(false);
  const attachmentsInitializedRef = useRef<boolean>(false);

  // Initialize local state from props on mount
  useEffect(() => {
    if (!notesInitializedRef.current && notes !== undefined && notes !== null) {
      const notesStr = typeof notes === 'string' ? notes : String(notes || '');
      setLocalNotes(notesStr);
      notesInitializedRef.current = true;
    }
  }, [notes]);

  useEffect(() => {
    if (!attachmentsInitializedRef.current && attachments !== undefined) {
      setLocalAttachments(attachments);
      attachmentsInitializedRef.current = true;
    }
  }, [attachments]);

  // Sync local state with props when they change (but don't override if user is actively editing or has local data)
  useEffect(() => {
    if (notesInitializedRef.current && !showNotesInput && notes !== undefined && notes !== null) {
      const notesStr = typeof notes === 'string' ? notes : String(notes || '');
      // Only sync if local notes are empty or props have a different value
      // Also check if notes prop matches localNotes to avoid unnecessary updates
      if ((!localNotes || String(localNotes || '').trim() === '') && notesStr && notesStr.trim() !== '') {
        // Only update if local is empty and prop has value
        setLocalNotes(notesStr);
      } else if (notesStr && notesStr.trim() !== '' && notesStr !== localNotes) {
        // Only update if prop has a different non-empty value
        setLocalNotes(notesStr);
      }
    }
  }, [notes, showNotesInput, localNotes]);

  useEffect(() => {
    if (attachmentsInitializedRef.current && attachments !== undefined) {
      // Only sync if local attachments are null or props have different attachments
      if (!localAttachments || (attachments && attachments.length > 0 && attachments !== localAttachments)) {
        setLocalAttachments(attachments);
      }
    }
  }, [attachments, localAttachments]);

  // Reset state when formValue changes for option-based components only
  // For ALL other components (input, textarea, date, signature, location, etc.), preserve notes and attachments
  useEffect(() => {
    const isOptionBasedComponent = ['select', 'radio', 'checkbox', 'segment'].includes(component.name || '');
    
    // Only reset for option-based components when the selected option actually changes
    if (isOptionBasedComponent) {
      const previousValue = previousFormValueRef.current;
      const currentValue = formValue;
      
      // Check if the value actually changed (not just initialized)
      // For checkbox, compare arrays properly
      let valueChanged = false;
      if (component.name === 'checkbox') {
        const prevArray = Array.isArray(previousValue) ? previousValue : (previousValue ? [previousValue] : []);
        const currArray = Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : []);
        valueChanged = JSON.stringify(prevArray.sort()) !== JSON.stringify(currArray.sort());
      } else {
        valueChanged = previousValue !== currentValue && previousValue !== undefined;
      }
      
      if (valueChanged) {
        // Only reset action states, but preserve notes and attachments if they exist in props
        setEmailSent(false);
        setIssueRaised(false);
        setShowRaiseIssueModal(false);
        setShowNotesInput(false);
        setActionsExpanded(false);
        
        // For option-based components, notes/attachments should be preserved per option
        // Only reset if props don't have them AND we're switching to a different option
        // Check if notes exist in props first
        const notesStr = typeof notes === 'string' ? notes : String(notes || '');
        if (!notesStr || notesStr.trim() === '') {
          // Only clear if we're switching options and there are no saved notes
          setLocalNotes('');
        } else {
          // Keep the notes from props
          setLocalNotes(notesStr);
        }
        
        if (!attachments || attachments.length === 0) {
          setLocalAttachments(null);
        } else {
          // Keep the attachments from props
          setLocalAttachments(attachments);
        }
      }
      
      previousFormValueRef.current = currentValue;
    }
    // For ALL non-option-based components (input, textarea, date, signature, location, etc.),
    // NEVER reset notes/attachments when form value changes - they should persist
    // Just update the ref to track value changes for future comparisons
    if (!isOptionBasedComponent) {
      previousFormValueRef.current = formValue;
    }
  }, [formValue, component.name, notes, attachments]);

  // Helper function to evaluate threshold conditions
  const evaluateThresholdCondition = useCallback((condition: any, value: any): boolean => {
    const { operator, value: conditionValue } = condition;
    const stringValue = String(value).toLowerCase();
    const stringConditionValue = String(conditionValue).toLowerCase();

    switch (operator) {
      case 'equals':
        return stringValue === stringConditionValue || String(value) === String(conditionValue);
      case 'notEquals':
        return stringValue !== stringConditionValue && String(value) !== String(conditionValue);
      case 'greaterThan':
        return Number(value) > Number(conditionValue);
      case 'lessThan':
        return Number(value) < Number(conditionValue);
      case 'greaterThanOrEqual':
        return Number(value) >= Number(conditionValue);
      case 'lessThanOrEqual':
        return Number(value) <= Number(conditionValue);
      default:
        return false;
    }
  }, []);

  // Find active threshold condition(s) for this component
  const getActiveThresholdConditions = useCallback((): any[] => {
    const hasValidFormValue = formValue !== undefined && 
                            formValue !== null && 
                            formValue !== '' && 
                            !(Array.isArray(formValue) && formValue.length === 0);
    if (!hasValidFormValue) return [];
    const condition = (component as any).condition;
    if (!condition || !condition.conditions || condition.conditions.length === 0) return [];
    
    const activeConditions: any[] = [];
    
    if (component.name === 'checkbox' && Array.isArray(formValue)) {
      formValue.forEach((value: any) => {
        const matchingCondition = condition.conditions.find((cond: any) => 
          evaluateThresholdCondition(cond, value)
        );
        if (matchingCondition && !activeConditions.find(c => c.id === matchingCondition.id)) {
          activeConditions.push(matchingCondition);
        }
      });
    } else {
      const matchingCondition = condition.conditions.find((cond: any) => 
        evaluateThresholdCondition(cond, formValue)
      );
      if (matchingCondition) {
        activeConditions.push(matchingCondition);
      }
    }
    
    return activeConditions;
  }, [component, formValue, evaluateThresholdCondition]);

  const handleNotesChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setLocalNotes(value);
    if (onNotesChange) {
      onNotesChange(value);
    }
    
    const activeConditions = getActiveThresholdConditions();
    const isCompleted = String(value || '').trim().length > 0;
    activeConditions.forEach(condition => {
      if (condition.enableNotes === true && onThresholdActionCompletion) {
        onThresholdActionCompletion(condition.id, 'notes', isCompleted);
      }
    });
    
    const componentCondition = (component as any).condition;
    const hasComponentThresholdConditions = componentCondition && componentCondition.conditions && componentCondition.conditions.length > 0;
    if (!hasComponentThresholdConditions && onBasicPropertyActionCompletion) {
      onBasicPropertyActionCompletion('notes', isCompleted);
    }
  }, [onNotesChange, getActiveThresholdConditions, onThresholdActionCompletion, component, onBasicPropertyActionCompletion]);

  const handleNotesSave = useCallback(() => {
    // Save notes and close input - always save even if empty
    if (onNotesChange) {
      const notesStr = typeof localNotes === 'string' ? localNotes : String(localNotes || '');
      // Call the callback to persist notes
      onNotesChange(notesStr);
      // Use setTimeout to ensure the callback completes before closing
      // This prevents the sync effect from overwriting the saved notes
      setTimeout(() => {
        setShowNotesInput(false);
      }, 0);
    } else {
      // If no callback, just close immediately
      setShowNotesInput(false);
    }
  }, [localNotes, onNotesChange]);

  const handleNotesBlur = useCallback(() => {
    // Save notes when blurring
    handleNotesSave();
  }, [handleNotesSave]);

  const handleNotesKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Save on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleNotesSave();
    }
  }, [handleNotesSave]);

  const handleNotesIconClick = useCallback(() => {
    setShowNotesInput(true);
  }, []);

  const handleNotesSummaryClick = useCallback(() => {
    setShowNotesInput(true);
  }, []);

  const handleAttachmentIconClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setLocalAttachments(fileArray);
      if (onAttachmentChange) {
        onAttachmentChange(fileArray);
    }
    
    const activeConditions = getActiveThresholdConditions();
    activeConditions.forEach(condition => {
      if (condition.enableAttachment === true && onThresholdActionCompletion) {
          onThresholdActionCompletion(condition.id, 'attachments', true);
      }
    });
    
    const componentCondition = (component as any).condition;
    const hasComponentThresholdConditions = componentCondition && componentCondition.conditions && componentCondition.conditions.length > 0;
    if (!hasComponentThresholdConditions && onBasicPropertyActionCompletion) {
        onBasicPropertyActionCompletion('attachments', true);
      }
    }
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onAttachmentChange, getActiveThresholdConditions, onThresholdActionCompletion, component, onBasicPropertyActionCompletion]);

  const handleRemoveAttachment = useCallback((index: number) => {
    if (localAttachments) {
      const newAttachments = localAttachments.filter((_, i) => i !== index);
      if (newAttachments.length === 0) {
        setLocalAttachments(null);
        if (onAttachmentChange) {
          onAttachmentChange(null);
        }
        
        const activeConditions = getActiveThresholdConditions();
        activeConditions.forEach(condition => {
          if (condition.enableAttachment === true && onThresholdActionCompletion) {
            onThresholdActionCompletion(condition.id, 'attachments', false);
          }
        });
        
        const componentCondition = (component as any).condition;
        const hasComponentThresholdConditions = componentCondition && componentCondition.conditions && componentCondition.conditions.length > 0;
        if (!hasComponentThresholdConditions && onBasicPropertyActionCompletion) {
          onBasicPropertyActionCompletion('attachments', false);
        }
      } else {
        setLocalAttachments(newAttachments);
        if (onAttachmentChange) {
          onAttachmentChange(newAttachments);
        }
      }
    }
  }, [localAttachments, onAttachmentChange, getActiveThresholdConditions, onThresholdActionCompletion, component, onBasicPropertyActionCompletion]);

  const handleSendEmailClick = useCallback(() => {
    // Immediately send email
    setEmailSent(true);
    
      const activeConditions = getActiveThresholdConditions();
      activeConditions.forEach(condition => {
        if (condition.enableSendEmail === true && onThresholdActionCompletion) {
        onThresholdActionCompletion(condition.id, 'email', true);
        }
      });
      
      const componentCondition = (component as any).condition;
      const hasComponentThresholdConditions = componentCondition && componentCondition.conditions && componentCondition.conditions.length > 0;
      if (!hasComponentThresholdConditions && onBasicPropertyActionCompletion) {
      onBasicPropertyActionCompletion('email', true);
    }
    
    // TODO: Call send email API when available
    setTimeout(() => {
      setEmailSent(false);
    }, 3000);
  }, [getActiveThresholdConditions, onThresholdActionCompletion, component, onBasicPropertyActionCompletion]);

  const handleRaiseIssueClick = useCallback(() => {
      setShowRaiseIssueModal(true);
  }, []);

  const handleRaiseIssueClose = useCallback(() => {
    setShowRaiseIssueModal(false);
  }, []);

  const handleRaiseIssueSuccess = useCallback(() => {
    setShowRaiseIssueModal(false);
    setIssueRaised(true);
    
    const componentCondition = (component as any).condition;
    const hasComponentThresholdConditions = componentCondition && componentCondition.conditions && componentCondition.conditions.length > 0;
    
    if (hasComponentThresholdConditions) {
      // For threshold conditions, notify each active condition that issue was raised
      const activeConditions = getActiveThresholdConditions();
      activeConditions.forEach(condition => {
        if (condition.enableRaiseIssue === true && onThresholdIssueRaised) {
          onThresholdIssueRaised(condition.id);
        }
      });
    } else if (onBasicPropertyActionCompletion) {
      // For basic property actions (no threshold conditions)
      onBasicPropertyActionCompletion('issue', true);
    }
  }, [component, onBasicPropertyActionCompletion, getActiveThresholdConditions, onThresholdIssueRaised]);

  // Only show in test mode
  if (mode !== 'test') {
    return null;
  }

  // Check if this is an option-based component
  const isOptionBasedComponent = ['select', 'radio', 'checkbox', 'segment'].includes(component.name || '');
  const componentOptions = (component as any).basic?.options || (component as any).options || [];

  let enableNotes = false;
  let enableAttachment = false;
  let enableRaiseIssue = false;
  let enableSendEmail = false;
  let selectedOption: any = null;

  const hasValidFormValue = formValue !== undefined && 
                            formValue !== null && 
                            formValue !== '' && 
                            !(Array.isArray(formValue) && formValue.length === 0);

  const condition = (component as any).condition;
  const hasThresholdConditions = condition && condition.conditions && condition.conditions.length > 0;
  
  if (hasThresholdConditions && hasValidFormValue) {
    if (component.name === 'checkbox' && Array.isArray(formValue)) {
      const selectedValues = formValue;
      for (const value of selectedValues) {
        const matchingCondition = condition.conditions.find((cond: any) => 
          evaluateThresholdCondition(cond, value)
        );
        if (matchingCondition) {
          enableNotes = enableNotes || (matchingCondition.enableNotes === true);
          enableAttachment = enableAttachment || (matchingCondition.enableAttachment === true);
          enableRaiseIssue = enableRaiseIssue || (matchingCondition.enableRaiseIssue === true);
          enableSendEmail = enableSendEmail || (matchingCondition.enableSendEmail === true);
        }
      }
    } else if (isOptionBasedComponent) {
      const matchingCondition = condition.conditions.find((cond: any) => 
        evaluateThresholdCondition(cond, formValue)
      );
      if (matchingCondition) {
        enableNotes = matchingCondition.enableNotes === true;
        enableAttachment = matchingCondition.enableAttachment === true;
        enableRaiseIssue = matchingCondition.enableRaiseIssue === true;
        enableSendEmail = matchingCondition.enableSendEmail === true;
      }
    } else {
      const matchingCondition = condition.conditions.find((cond: any) => 
        evaluateThresholdCondition(cond, formValue)
      );
      if (matchingCondition) {
        enableNotes = matchingCondition.enableNotes === true;
        enableAttachment = matchingCondition.enableAttachment === true;
        enableRaiseIssue = matchingCondition.enableRaiseIssue === true;
        enableSendEmail = matchingCondition.enableSendEmail === true;
      }
    }
  }

  if (!hasThresholdConditions) {
    // First, get component-level actions as fallback
    const componentBasic = component.basic as any;
    const componentLevelNotes = componentBasic?.enableNotes === true;
    const componentLevelAttachment = componentBasic?.enableAttachment === true;
    const componentLevelRaiseIssue = componentBasic?.enableRaiseIssue === true;
    const componentLevelSendEmail = componentBasic?.enableSendEmail === true;
    
    if (isOptionBasedComponent) {
      if (hasValidFormValue) {
        // Value is selected, check option-level actions
        if (component.name === 'checkbox') {
          const selectedValues = Array.isArray(formValue) ? formValue : [formValue];
          selectedValues.forEach((value: any) => {
            const option = componentOptions.find((opt: any) => {
              if (!opt || !opt.value) return false;
              if (opt.value === value) return true;
              if (String(opt.value) === String(value)) return true;
              if (opt.label === value || String(opt.label) === String(value)) return true;
              return false;
            });
            if (option) {
              // Check if option has actions enabled
              enableNotes = enableNotes || (option.enableNotes === true);
              enableAttachment = enableAttachment || (option.enableAttachment === true);
              enableRaiseIssue = enableRaiseIssue || (option.enableRaiseIssue === true);
              enableSendEmail = enableSendEmail || (option.enableSendEmail === true);
              if (!selectedOption) {
                selectedOption = option;
              }
            }
          });
          // Always include component-level actions as fallback
          enableNotes = enableNotes || componentLevelNotes;
          enableAttachment = enableAttachment || componentLevelAttachment;
          enableRaiseIssue = enableRaiseIssue || componentLevelRaiseIssue;
          enableSendEmail = enableSendEmail || componentLevelSendEmail;
        } else {
          selectedOption = componentOptions.find((opt: any) => {
            if (!opt || !opt.value) return false;
            if (opt.value === formValue) return true;
            if (String(opt.value) === String(formValue)) return true;
            if (opt.label === formValue || String(opt.label) === String(formValue)) return true;
            return false;
          });
          if (selectedOption) {
            const optionNotes = selectedOption.enableNotes === true;
            const optionAttachment = selectedOption.enableAttachment === true;
            const optionRaiseIssue = selectedOption.enableRaiseIssue === true;
            const optionSendEmail = selectedOption.enableSendEmail === true;
            
            // Use option-level actions if set, otherwise fallback to component-level
            enableNotes = optionNotes || componentLevelNotes;
            enableAttachment = optionAttachment || componentLevelAttachment;
            enableRaiseIssue = optionRaiseIssue || componentLevelRaiseIssue;
            enableSendEmail = optionSendEmail || componentLevelSendEmail;
          } else {
            // No option found, use component-level actions
            enableNotes = componentLevelNotes;
            enableAttachment = componentLevelAttachment;
            enableRaiseIssue = componentLevelRaiseIssue;
            enableSendEmail = componentLevelSendEmail;
          }
        }
      } else {
        // No value selected, but still show component-level actions if enabled
        enableNotes = componentLevelNotes;
        enableAttachment = componentLevelAttachment;
        enableRaiseIssue = componentLevelRaiseIssue;
        enableSendEmail = componentLevelSendEmail;
      }
    } else {
      // Not an option-based component, use component-level actions
      enableNotes = componentLevelNotes;
      enableAttachment = componentLevelAttachment;
      enableRaiseIssue = componentLevelRaiseIssue;
      enableSendEmail = componentLevelSendEmail;
    }
  }

  if (!enableNotes && !enableAttachment && !enableRaiseIssue && !enableSendEmail) {
    return null;
  }

  const activeConditions = getActiveThresholdConditions();
  const componentCondition = (component as any).condition;
  const hasComponentThresholdConditions = componentCondition && componentCondition.conditions && componentCondition.conditions.length > 0;
  
  const isNotesMandatory = hasComponentThresholdConditions 
    ? activeConditions.some(c => c.enableNotes === true) 
    : false;
  const isAttachmentsMandatory = hasComponentThresholdConditions 
    ? activeConditions.some(c => c.enableAttachment === true) 
    : false;
  const isSendEmailMandatory = hasComponentThresholdConditions 
    ? activeConditions.some(c => c.enableSendEmail === true) 
    : false;
  const isRaiseIssueMandatory = hasComponentThresholdConditions 
    ? activeConditions.some(c => c.enableRaiseIssue === true) 
    : false;

  const hasNotes = String(localNotes || '').trim().length > 0;
  const hasAttachments = localAttachments && localAttachments.length > 0;
  const attachmentCount = localAttachments ? localAttachments.length : 0;

  return (
    <>
      <div className="component-action-features">
        {/* Icon Toolbar */}
        <div className="action-icons-toolbar">
        {enableNotes && (
            <button
              type="button"
              className={`action-icon-button ${hasNotes ? 'active' : ''} ${isNotesMandatory ? 'mandatory' : ''}`}
              onClick={handleNotesIconClick}
              title="Add Notes"
            >
              <MessageSquare size={14} fill={hasNotes ? 'currentColor' : 'none'} />
              {isNotesMandatory && <span className="mandatory-indicator">*</span>}
            </button>
          )}

          {enableAttachment && (
            <button
              type="button"
              className={`action-icon-button ${hasAttachments ? 'active' : ''} ${isAttachmentsMandatory ? 'mandatory' : ''}`}
              onClick={handleAttachmentIconClick}
              title="Add Attachment"
            >
              <Paperclip size={14} fill={hasAttachments ? 'currentColor' : 'none'} />
              {attachmentCount > 0 && <span className="attachment-count-badge">{attachmentCount}</span>}
              {isAttachmentsMandatory && <span className="mandatory-indicator">*</span>}
            </button>
          )}

          {enableSendEmail && (
            <button
              type="button"
              className={`action-icon-button ${emailSent ? 'active sent' : ''} ${isSendEmailMandatory ? 'mandatory' : ''}`}
              onClick={handleSendEmailClick}
              disabled={emailSent}
              title="Send Email"
            >
              {emailSent ? (
                <>
                  <Check size={14} />
                </>
              ) : (
                <Mail size={14} fill={emailSent ? 'currentColor' : 'none'} />
              )}
              {isSendEmailMandatory && <span className="mandatory-indicator">*</span>}
            </button>
          )}

          {enableRaiseIssue && (
            <button
              type="button"
              className={`action-icon-button ${issueRaised ? 'active' : ''} ${isRaiseIssueMandatory ? 'mandatory' : ''}`}
              onClick={handleRaiseIssueClick}
              title="Raise an Issue"
            >
              <AlertCircle size={14} fill={issueRaised ? 'currentColor' : 'none'} />
              {isRaiseIssueMandatory && <span className="mandatory-indicator">*</span>}
            </button>
          )}

          {/* Accordion Toggle Button - Show if there are notes or attachments */}
          {((hasNotes && enableNotes) || (hasAttachments && enableAttachment)) && (
            <button
              type="button"
              className="action-icon-button accordion-toggle"
              onClick={() => setActionsExpanded(!actionsExpanded)}
              title={actionsExpanded ? 'Collapse' : 'Expand'}
            >
              {actionsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>

        {/* Notes Input Overlay */}
        {showNotesInput && enableNotes && (
          <div className="notes-input-overlay">
            <textarea
              className="action-notes-textarea"
              value={localNotes}
              onChange={handleNotesChange}
              onBlur={handleNotesBlur}
              onKeyDown={handleNotesKeyDown}
              placeholder="Explain which protocols were not followed (Press Ctrl+Enter to save)"
              rows={4}
              autoFocus
            />
            <div className="notes-input-actions">
              <button
                type="button"
                className="notes-save-button"
                onClick={handleNotesSave}
                title="Save notes (Ctrl+Enter)"
              >
                Save
              </button>
              <button
                type="button"
                className="notes-cancel-button"
                onClick={() => {
                  setShowNotesInput(false);
                }}
                title="Cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Notes and Attachments Content - Controlled by Accordion */}
        {actionsExpanded && (
          <div className="actions-content">
            {/* Notes Summary */}
            {!showNotesInput && hasNotes && enableNotes && (
              <div className="notes-summary-section">
                <div className="notes-full-text" onClick={handleNotesSummaryClick}>
                  <span className="notes-label">Notes:</span> {localNotes}
                </div>
              </div>
            )}

            {/* Attachment Thumbnails */}
            {hasAttachments && enableAttachment && (
              <div className="attachments-section">
                <AttachmentThumbnails
                  attachments={localAttachments}
                  onRemove={handleRemoveAttachment}
                />
              </div>
            )}
          </div>
        )}

        {/* Hidden File Input */}
        {enableAttachment && (
                    <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        )}
      </div>

      {/* Raise Issue Modal */}
      {showRaiseIssueModal && (
        <RaiseIssueModal
          isOpen={showRaiseIssueModal}
          onClose={handleRaiseIssueClose}
          onSuccess={handleRaiseIssueSuccess}
          component={selectedOption ? {
            ...component,
            basic: {
              ...(component.basic as any),
              label: `${(component.basic as any)?.label || 'Component'} - ${selectedOption.label || selectedOption.value}`,
              value: selectedOption.value || (component.basic as any)?.value || ''
            } as any
          } : component}
          formTemplateId={formTemplateId || ''}
          notes={localNotes}
          attachments={localAttachments}
        />
      )}
    </>
  );
};
